import { useCallback, useEffect, useRef } from 'react'

import { LRUMap } from '@/utils/lru'

import { useImpressionTracker } from './ImpressionProvider'

interface UseListImpressionTrackingOptions {
  minVisibleDuration?: number
  threshold?: number
  id: string
  debug?: boolean
  enabled?: boolean
  containerSelector?: string
  rootElement?: HTMLElement | Document
  manualStart?: boolean
}

export const useListImpressionTracking = ({
  minVisibleDuration = 1000,
  threshold = 0.5,
  id,
  debug = false,
  enabled = true,
  containerSelector = '[data-impression-container="true"]',
  rootElement,
  manualStart = false
}: UseListImpressionTrackingOptions) => {
  const log = useCallback(
    (...args: any[]) => debug && console.log(...args),
    [debug]
  )
  const { addImpression, registerObserver, unregisterObserver } =
    useImpressionTracker()

  const observerRef = useRef<IntersectionObserver | null>(null)
  const timeoutsRef = useRef(new LRUMap<string, number>(50))
  const mutationObserverRef = useRef<MutationObserver | null>(null)
  const hasStartedRef = useRef(!manualStart)

  const handleIntersection = useCallback(
    (entries: IntersectionObserverEntry[]) => {
      entries.forEach(entry => {
        const target = entry.target as HTMLElement

        const dataListingId = target.dataset.impressionListingid
        const page = target.dataset.impressionPage
        const section = target.dataset.impressionSection
        const sponsored = target.dataset.impressionSponsored || 'false'

        if (!dataListingId || !page || !section) return

        const listingIds = dataListingId.split(',')
        const key = `${listingIds
          .map(id => id)
          .join('-')}:${page}:${section}:${sponsored}`

        if (entry.isIntersecting) {
          log(
            key,
            'is intersecting. timeout to sync: %d',
            listingIds,
            minVisibleDuration,
            entry.target
          )
          if (!timeoutsRef.current.has(key)) {
            const timeoutId = window.setTimeout(() => {
              observerRef.current?.unobserve(target)
              const metadata = {
                page,
                section,
                sponsored: sponsored === 'true'
              }
              for (const listingId of listingIds) {
                log('add impression', listingId, metadata)
                addImpression(listingId, metadata)
              }
              target.dataset.impressionTracked = 'true'
              timeoutsRef.current.delete(key)
            }, minVisibleDuration)
            timeoutsRef.current.set(key, timeoutId)
          }
        } else {
          log(key, 'NOT intersecting', entry.target)
          const timeoutId = timeoutsRef.current.get(key)
          if (timeoutId !== undefined) {
            clearTimeout(timeoutId)
            timeoutsRef.current.delete(key)
          }
        }
      })
    },
    [addImpression, log, minVisibleDuration]
  )

  useEffect(() => {
    if (!enabled) {
      // Clean up when disabled
      if (observerRef.current) {
        observerRef.current.disconnect()
        unregisterObserver(id)
        observerRef.current = null
      }
      if (mutationObserverRef.current) {
        mutationObserverRef.current.disconnect()
        mutationObserverRef.current = null
      }
      timeoutsRef.current.forEach(timeout => clearTimeout(timeout))
      timeoutsRef.current.clear()
      hasStartedRef.current = !manualStart
      return
    }

    observerRef.current = new IntersectionObserver(handleIntersection, {
      threshold
    })
    registerObserver(id, observerRef.current)

    // Only set up observers if not in manual start mode or already started
    if (hasStartedRef.current) {
      const root =
        rootElement || (typeof document !== 'undefined' ? document : null)
      if (root) {
        observeElements(observerRef.current, root)
        mutationObserverRef.current = observeContainers(
          observerRef.current,
          containerSelector,
          root
        )
      }
    }

    const timeouts = timeoutsRef.current

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect()
        unregisterObserver(id)
      }
      if (mutationObserverRef.current) {
        mutationObserverRef.current.disconnect()
      }
      timeouts.forEach(timeout => clearTimeout(timeout))
      timeouts.clear()
      hasStartedRef.current = !manualStart
    }
  }, [
    handleIntersection,
    id,
    registerObserver,
    threshold,
    unregisterObserver,
    enabled,
    containerSelector,
    rootElement,
    manualStart
  ])

  const start = useCallback(
    (newRootElement: HTMLElement | Document) => {
      if (observerRef.current) {
        log('Manually refreshing observers')
        hasStartedRef.current = true
        if (newRootElement) {
          observeElements(observerRef.current, newRootElement)
          if (mutationObserverRef.current) {
            mutationObserverRef.current.disconnect()
          }
          mutationObserverRef.current = observeContainers(
            observerRef.current,
            containerSelector,
            newRootElement
          )
        }
      }
    },
    [containerSelector, log]
  )

  return {
    start
  }
}

function refreshObserver(
  observer: IntersectionObserver,
  root: HTMLElement | Document
) {
  observer.disconnect()
  observeElements(observer, root)
}

function observeElements(
  observer: IntersectionObserver | null,
  root: HTMLElement | Document
) {
  if (!observer || !root) return

  const elements = root.querySelectorAll('[data-impression="true"]')
  elements.forEach(el => observer.observe(el))
}

function observeContainers(
  observer: IntersectionObserver | null,
  containerSelector: string,
  rootElement: HTMLElement | Document
): MutationObserver | null {
  if (!observer) return null
  const containers = rootElement.querySelectorAll(containerSelector)
  if (containers.length === 0) return null

  let timeout: number
  const mutationObserver = new MutationObserver(() => {
    if (timeout) clearTimeout(timeout)
    timeout = window.setTimeout(() => {
      refreshObserver(observer, rootElement)
    }, 150)
  })
  containers.forEach(el =>
    mutationObserver.observe(el, {
      childList: true,
      subtree: true
    })
  )
  return mutationObserver
}
